import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Truck, Store, UserRound } from "lucide-react";
import { api } from "@/lib/api";
import { GoogleButton } from "@/components/GoogleButton";
import { useAuth } from "@/context/auth-context";
import { homeFor } from "@/components/RoleGuard";
import { Brand } from "@/components/common/Brand";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const ROLES = [
  { role: "customer", label: "Customer", icon: UserRound, desc: "Browse & order", cls: "bg-primary text-primary-foreground" },
  { role: "vendor", label: "Vendor", icon: Store, desc: "Manage a store", cls: "bg-popblue text-white" },
  { role: "driver", label: "Driver", icon: Truck, desc: "Deliver orders", cls: "bg-popgreen text-white" },
  { role: "admin", label: "Admin", icon: ShieldCheck, desc: "Run the marketplace", cls: "bg-popyellow text-foreground" },
];

export default function Login() {
  const navigate = useNavigate();
  const { user, signInWithToken } = useAuth();
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (user) navigate(homeFor(user.role), { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api("/auth/config").then(setConfig).catch(() => setConfig({ dev_mode: false }));
  }, []);

  const devLogin = async (role) => {
    setBusy(role);
    try {
      await api("/dev/seed", { method: "POST" }).catch(() => {});
      const res = await api("/dev/test-session", { method: "POST", json: { role } });
      await signInWithToken(res.session_token, res.user);
      navigate(homeFor(res.user.role), { replace: true });
    } catch (e) {
      toast.error(e.message || "Login failed");
    } finally {
      setBusy(null);
    }
  };

  const onGoogleSuccess = async (accessToken) => {
    if (!accessToken) return toast.error("Google sign-in failed");
    try {
      const res = await api("/auth/google/web", { method: "POST", json: { access_token: accessToken } });
      await signInWithToken(res.session_token, res.user);
      navigate(homeFor(res.user.role), { replace: true });
    } catch (e) {
      toast.error(e.message || "Google sign-in failed");
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden overflow-hidden bg-popblue lg:flex lg:flex-col">
        <div className="stripe-green h-5 w-full shrink-0" />
        <div className="relative flex flex-1 flex-col justify-between p-12 text-white">
          <Brand tone="inverse" />
          <div>
            <span className="inline-block -rotate-2 rounded-xl bg-popyellow px-4 py-1.5 font-display text-lg text-foreground shadow-pop-sm">
              Fresh · Local · Fun
            </span>
            <h1 className="mt-5 font-display text-5xl font-bold leading-[1.05]">
              Your neighbourhood grocer, delivered.
            </h1>
            <p className="mt-4 max-w-md text-lg text-white/85">
              Oppad connects local vendors, drivers and customers across South Africa — one bright, community-owned marketplace.
            </p>
          </div>
          <p className="font-display text-sm text-white/70">Hyper-local delivery · Stellenbosch &amp; beyond</p>
        </div>
        <div className="checker checker-red checker-sm h-16 w-full shrink-0" />
      </div>

      {/* Form panel */}
      <div className="relative flex items-center justify-center overflow-hidden p-6">
        <div className="checker checker-red absolute inset-x-0 bottom-0 h-10 opacity-90 lg:hidden" />
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <Brand />
          </div>
          <h2 className="font-display text-4xl font-bold">Welcome to Oppad</h2>
          <p className="mt-1 text-muted-foreground">Sign in to start shopping local.</p>

          <div className="mt-6" data-testid="google-signin-button">
            {config?.google_configured ? (
              <GoogleButton onToken={onGoogleSuccess} className="w-full rounded-full border-2 font-display" />
            ) : (
              <>
                <Button className="w-full rounded-full border-2 font-display" variant="outline" disabled>
                  Continue with Google
                </Button>
                <p className="mt-2 text-center text-xs text-muted-foreground">
                  Google sign-in not configured on this server yet.
                </p>
              </>
            )}
          </div>

          {config?.dev_mode && (
            <Card className="mt-6 rounded-2xl border-2 border-dashed p-4">
              <p className="mb-3 font-display text-sm uppercase tracking-widest text-muted-foreground">
                Demo quick login
              </p>
              <div className="grid grid-cols-2 gap-3">
                {ROLES.map((r) => (
                  <button
                    key={r.role}
                    disabled={!!busy}
                    onClick={() => devLogin(r.role)}
                    data-testid={`dev-login-${r.role}`}
                    className={cn(
                      "flex flex-col items-start gap-0.5 rounded-2xl px-4 py-3 text-left transition-transform active:translate-y-0.5 disabled:opacity-60",
                      r.cls,
                      "shadow-pop-sm hover:-translate-y-0.5",
                    )}
                  >
                    <span className="flex items-center gap-2 font-display text-base">
                      <r.icon className="h-4 w-4" /> {busy === r.role ? "…" : r.label}
                    </span>
                    <span className="text-xs opacity-80">{r.desc}</span>
                  </button>
                ))}
              </div>
            </Card>
          )}

          <div className="mt-6 text-center text-sm">
            <button
              className="font-display text-primary hover:underline"
              onClick={() => navigate("/staff-login")}
              data-testid="staff-login-link"
            >
              Staff &amp; admin login
            </button>
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            By continuing you agree to our{" "}
            <a href="/legal/terms" className="text-primary" data-testid="login-terms-link">Terms</a> and{" "}
            <a href="/legal/policy" className="text-primary" data-testid="login-privacy-link">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
